import dayjs from "dayjs";
import { getLastNDaysLabels } from "../../utils/helper";

export const getFlexibilitySitChartData = (chartData = [], period, gender) => {
  const days = period === "month" ? 30 : 7;
  const labels = getLastNDaysLabels(days);

  const filteredChartData = chartData.filter(
    (entry) =>
      entry.gender === gender &&
      dayjs(entry.createdAt).isAfter(dayjs().subtract(days, "day")),
  );

  const dataMap = new Map();
  filteredChartData.forEach((entry) => {
    const label = dayjs(entry.createdAt).format("MMM D");
    const value =
      parseFloat(
        entry.method === "shoulder" ? entry.shoulderclasp : entry.reachdistance,
      ) || 0;
    if (!dataMap.has(label)) {
      dataMap.set(label, value);
    }
  });

  const finalData = labels.map((label) => ({
    label,
    value: dataMap.get(label) ?? 0,
  }));

  return {
    labels: finalData.map((d) => d.label),
    datasets: [
      {
        label: "Reach Distance (cm)",
        data: finalData.map((d) => d.value),
        borderColor: "var(--primaryColor)",
        backgroundColor: (context) => {
          const chart = context.chart;
          const { ctx, chartArea } = chart;
          if (!chartArea) return null;

          const gradient = ctx.createLinearGradient(
            0,
            chartArea.top,
            0,
            chartArea.bottom,
          );
          gradient.addColorStop(0, "rgba(162, 110, 255, 0.3)");
          gradient.addColorStop(1, "rgba(162, 110, 255, 0)");
          return gradient;
        },
        fill: true,
        tension: 0.4,
        pointBackgroundColor: "var(--primaryColor)",
        pointRadius: (context) => (context.raw === 0 ? 0.5 : 5),
        pointHoverRadius: (context) => (context.raw === 0 ? 5 : 6),
        borderWidth: 2,
      },
    ],
  };
};

export const getFlexibilityStats = (chartData = []) => {
  const values = chartData
    .map((item) => parseFloat(item.reachdistance ?? item.shoulderclasp))
    .filter((value) => !isNaN(value));

  if (values.length === 0) {
    return {
      flexibilityMin: 0,
      flexibilityMax: 0,
      flexibilityStepSize: 1,
    };
  }

  const maxValue = Math.max(...values);

  let flexibilityStepSize = 1;
  if (maxValue <= 10) flexibilityStepSize = 1;
  else if (maxValue <= 50) flexibilityStepSize = 5;
  else if (maxValue <= 100) flexibilityStepSize = 10;
  else flexibilityStepSize = 20;

  const flexibilityMin = 0;
  const flexibilityMax =
    Math.ceil(maxValue / flexibilityStepSize) * flexibilityStepSize;

  return {
    flexibilityMin,
    flexibilityMax,
    flexibilityStepSize,
  };
};

export const getStrengthChartData = (chartData = [], period, t) => {
  if (!Array.isArray(chartData)) return { labels: [], datasets: [] };

  const days = period === "month" ? 30 : 7;
  const labels = getLastNDaysLabels(days);

  const filteredChartData = chartData.filter((entry) =>
    dayjs(entry.createdAt).isAfter(dayjs().subtract(days, "day")),
  );

  // Define zones based on translation
  const zones = [
    t("excellent"),
    t("good"),
    t("average"),
    t("belowaverage"),
    t("poor"),
    t("unknown"),
  ];

  // Initialize result groups
  const resultGroups = {};
  zones.forEach((zone) => {
    resultGroups[zone] = new Array(labels.length).fill(0);
  });

  filteredChartData.forEach((entry) => {
    const translatedZones = {
      [t("excellent").toLowerCase()]: t("excellent"),
      [t("good").toLowerCase()]: t("good"),
      [t("average").toLowerCase()]: t("average"),
      [t("belowaverage").toLowerCase()]: t("belowaverage"),
      [t("poor").toLowerCase()]: t("poor"),
      [t("unknown").toLowerCase()]: t("unknown"),
    };

    let rawLabel = entry.result
      ? entry.result.toString().trim().toLowerCase()
      : t("unknown").toLowerCase();
    const label = translatedZones[rawLabel] || t("unknown");

    const dateLabel = dayjs(entry.createdAt).format("MMM D");
    const index = labels.indexOf(dateLabel);
    if (index !== -1 && resultGroups[label]) {
      resultGroups[label][index] = Number(entry.input);
    }
  });

  const getCSSVariable = (name) =>
    getComputedStyle(document.documentElement).getPropertyValue(name).trim();

  const primary = getCSSVariable("--primaryColor");

  // Create lighter tints of primary
  const lighten = (color, amount) => {
    const num = parseInt(color.replace("#", ""), 16);
    let r = (num >> 16) + amount;
    let g = ((num >> 8) & 0x00ff) + amount;
    let b = (num & 0x0000ff) + amount;
    r = r < 255 ? r : 255;
    g = g < 255 ? g : 255;
    b = b < 255 ? b : 255;
    return `rgb(${r}, ${g}, ${b})`;
  };

  const zoneColors = {
    [t("excellent")]: primary, // #5e17eb
    [t("good")]: lighten(primary, 40), // lighter violet
    [t("average")]: lighten(primary, 70),
    [t("belowaverage")]: lighten(primary, 100),
    [t("poor")]: "#D3D3D3",
    [t("unknown")]: "#999",
  };

  const datasets = Object.entries(resultGroups)
    .filter(([, data]) => data.some((v) => v > 0))
    .map(([label, data]) => ({
      label,
      data,
      backgroundColor: zoneColors[label] || "#999",
      borderColor: zoneColors[label] || "#999",
      borderWidth: 1,
      barThickness: period === "week" ? 60 : 25,
    }));

  return { labels, datasets };
};

export const getStrengthStats = (chartData = []) => {
  const strengthValues = chartData.map((item) =>
    parseFloat(item.input?.toString().replace(/[^\d.]/g, "")),
  );

  if (strengthValues.length === 0) {
    return { strengthMin: 0, strengthMax: 60, strengthStepSize: 10 };
  }

  const maxValue = Math.max(...strengthValues);

  let strengthStepSize = 5;
  if (maxValue > 0 && maxValue <= 20) strengthStepSize = 5;
  else if (maxValue > 20 && maxValue <= 60) strengthStepSize = 10;
  else if (maxValue > 60 && maxValue <= 100) strengthStepSize = 20;
  else if (maxValue > 100) strengthStepSize = 50;

  const strengthMax = Math.ceil(maxValue / strengthStepSize) * strengthStepSize;

  return {
    strengthMin: 0,
    strengthMax,
    strengthStepSize,
  };
};

export const getCardioFitnessChartData = (chartData = [], period) => {
  const days = period === "month" ? 30 : 7;
  const labels = getLastNDaysLabels(days);

  const filteredChartData = chartData.filter((entry) =>
    dayjs(entry.createdAt).isSameOrAfter(
      dayjs().subtract(days - 1, "day"),
      "day",
    ),
  );

  const dataMap = new Map();

  filteredChartData.forEach((entry) => {
    const label = dayjs(entry.createdAt).format("MMM D");
    const value = parseFloat(entry.result || "0");
    if (!dataMap.has(label)) {
      dataMap.set(label, value);
    }
  });

  const finalData = labels.map((label) => ({
    label,
    value: dataMap.get(label) ?? 0,
  }));

  return {
    labels: finalData.map((d) => d.label),
    datasets: [
      {
        label: "Cardio Fitness",
        data: finalData.map((d) => d.value),
        borderColor: "var(--primaryColor)",
        backgroundColor: (context) => {
          const chart = context.chart;
          const { ctx, chartArea } = chart;
          if (!chartArea) return null;

          const gradient = ctx.createLinearGradient(
            0,
            chartArea.top,
            0,
            chartArea.bottom,
          );
          gradient.addColorStop(0, "rgba(162, 110, 255, 0.3)");
          gradient.addColorStop(1, "rgba(162, 110, 255, 0)");
          return gradient;
        },
        fill: true,
        tension: 0.4,
        pointBackgroundColor: "var(--primaryColor)",
        pointRadius: function (context) {
          return context.raw === 0 ? 0.5 : 5;
        },
        pointHoverRadius: function (context) {
          return context.raw === 0 ? 5 : 6;
        },
      },
    ],
  };
};

export const getCardioStats = (chartData = []) => {
  const cardioValues = chartData
    .map((item) => parseFloat(item.result?.toString().replace(/[^\d.]/g, "")))
    .filter((val) => !isNaN(val));

  if (cardioValues.length === 0) {
    return { cardioMin: 0, cardioMax: 100, cardioStepSize: 20 };
  }

  const maxValue = Math.max(...cardioValues);

  let cardioStepSize = 10;
  if (maxValue > 0 && maxValue < 50) cardioStepSize = 5;
  else if (maxValue >= 50 && maxValue <= 100) cardioStepSize = 10;
  else if (maxValue > 100 && maxValue <= 200) cardioStepSize = 20;
  else if (maxValue > 200) cardioStepSize = 50;

  const cardioMax = Math.ceil(maxValue / cardioStepSize) * cardioStepSize;

  return {
    cardioMin: 0,
    cardioMax,
    cardioStepSize,
  };
};

export const getBalanceChartData = (
  apiChartData = [],
  period,
  selectedBalance,
  t,
) => {
  if (!Array.isArray(apiChartData)) return { labels: [], datasets: [] };

  const days = period === "month" ? 30 : 7;
  const labels = getLastNDaysLabels(days).map((d) => dayjs(d).format("MMM D"));
  const flattenedData = apiChartData.flatMap((entry) => entry.values || []);

  const latestPerDay = {};
  flattenedData.forEach((entry) => {
    const dateKey = dayjs(entry.createdAt).format("MMM D");
    if (
      !latestPerDay[dateKey] ||
      dayjs(entry.createdAt).isAfter(latestPerDay[dateKey].createdAt)
    ) {
      latestPerDay[dateKey] = entry;
    }
  });

  const internalZones = [
    "Excellent",
    "Good",
    "Average",
    "Below average",
    "Poor",
    "Unknown",
  ];

  const translatedZones = {
    [t("excellent")]: "Excellent",
    [t("good")]: "Good",
    [t("average")]: "Average",
    [t("belowaverage")]: "Below average",
    [t("poor")]: "Poor",
    [t("unknown")]: "Unknown",
    Excellent: "Excellent",
    Good: "Good",
    Average: "Average",
    "Below average": "Below average",
    Poor: "Poor",
    Unknown: "Unknown",
  };

  const resultGroups = {};
  internalZones.forEach((zone) => {
    resultGroups[zone] = new Array(labels.length).fill(0);
  });

  labels.forEach((label, idx) => {
    const entry = latestPerDay[label];
    if (entry) {
      const value =
        selectedBalance === "singleleggedstand"
          ? Number(entry.timeOnOneLeg) || 0
          : Number(entry.stepsWithoutDeviation) || 0;

      const rawResult = entry.result || "Unknown";
      const normalizedResult = translatedZones[rawResult] || "Unknown";

      resultGroups[normalizedResult][idx] = value;
    }
  });

  const getCSSVariable = (name) =>
    getComputedStyle(document.documentElement).getPropertyValue(name).trim();

  const primary = getCSSVariable("--primaryColor");

  // Create lighter tints of primary
  const lighten = (color, amount) => {
    const num = parseInt(color.replace("#", ""), 16);
    let r = (num >> 16) + amount;
    let g = ((num >> 8) & 0x00ff) + amount;
    let b = (num & 0x0000ff) + amount;
    r = r < 255 ? r : 255;
    g = g < 255 ? g : 255;
    b = b < 255 ? b : 255;
    return `rgb(${r}, ${g}, ${b})`;
  };

  const zoneColors = {
    excellent: primary,
    good: lighten(primary, 40),
    average: lighten(primary, 70),
    belowaverage: lighten(primary, 100),
    poor: "#D3D3D3",
    unknown: "#999",
  };

  const datasets = Object.entries(resultGroups)
    .filter(([, data]) => data.some((v) => v > 0))
    .map(([label, data]) => {
      const key = label.toLowerCase().replace(/\s+/g, "");
      const translatedLabel = t(key);
      return {
        label: translatedLabel,
        data,
        backgroundColor: zoneColors[key] || "#999",
        borderColor: zoneColors[key] || "#999",
        borderWidth: 1,
        barThickness: period === "week" ? 60 : 25,
      };
    });

  return { labels, datasets };
};

export const getBalanceStats = (chartData = [], selectedBalance) => {
  const allValues = chartData
    .flatMap((entry) => entry.values || [])
    .map((item) =>
      selectedBalance === "singleleggedstand"
        ? Number(item.timeOnOneLeg) || 0
        : Number(item.stepsWithoutDeviation) || 0,
    )
    .filter((v) => !isNaN(v) && v > 0);

  if (allValues.length === 0) {
    return { balMin: 0, balMax: 20, balStepSize: 5 };
  }

  const maxValue = Math.max(...allValues);

  let balStepSize = 5;
  if (maxValue > 0 && maxValue <= 20) balStepSize = 5;
  else if (maxValue > 20 && maxValue <= 60) balStepSize = 10;
  else if (maxValue > 60 && maxValue <= 100) balStepSize = 20;
  else if (maxValue > 100) balStepSize = 50;

  const balMax = Math.ceil(maxValue / balStepSize) * balStepSize;

  return {
    balMin: 0,
    balMax,
    balStepSize,
  };
};
